import React, { useState, useEffect, useRef } from 'react'
import api from '../../services/api'

function SetoranReguler() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState(null)
  const [jumlahSetor, setJumlahSetor] = useState('')
  const [sumberDana, setSumberDana] = useState('kas')
  const [processing, setProcessing] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [transactionData, setTransactionData] = useState(null)
  const [error, setError] = useState(null)
  const [allocation, setAllocation] = useState(null)
  const [lastTglBayarWajib, setLastTglBayarWajib] = useState(null)
  const [wajibDoneThisMonth, setWajibDoneThisMonth] = useState(false)
  const memberSelectRef = useRef(null)

  useEffect(() => {
    fetchActiveMembers()
  }, [])

  useEffect(() => {
    if (selectedMember) {
      fetchLastWajibPayment()
    } else {
      setLastTglBayarWajib(null)
    }
  }, [selectedMember])

  const fetchLastWajibPayment = async () => {
    try {
      // Call setoranReguler endpoint with preview=true to get allocation details including wajib status
      const response = await api.post(`/savings/reguler/${selectedMember.NBA}/setoran?preview=true`, {
        jumlahSetor: 0,
        sumberDana: 'kas'
      })
      if (response.data.data) {
        setLastTglBayarWajib(response.data.data.lastTglBayarWajib)
        setWajibDoneThisMonth(response.data.data.wajibDoneThisMonth || false)
      }
    } catch (error) {
      console.error('Error fetching last Wajib payment:', error)
      setLastTglBayarWajib(null)
      setWajibDoneThisMonth(false)
    }
  }

  useEffect(() => {
    calculateAllocation()
  }, [jumlahSetor, wajibDoneThisMonth])

  useEffect(() => {
    if (memberSelectRef.current && members.length > 0) {
      // Initialize Select2
      $(memberSelectRef.current).select2({
        placeholder: '-- Pilih Anggota --',
        allowClear: true,
        width: '100%'
      })

      // Handle change event
      $(memberSelectRef.current).on('change', (e) => {
        const nba = e.target.value
        const member = members.find(m => m.NBA === nba)
        setSelectedMember(member || null)
      })

      // Cleanup
      return () => {
        if (memberSelectRef.current) {
          $(memberSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [members])

  const fetchActiveMembers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/members', {
        params: { limit: 1000, statusAktif: 1 }
      })
      const data = response.data.data || []
      setMembers(data)
    } catch (error) {
      console.error('Error fetching active members:', error)
      setError(error.response?.data?.message || error.message || 'Gagal memuat data anggota')
    } finally {
      setLoading(false)
    }
  }

  const calculateAllocation = async () => {
    if (!jumlahSetor || !selectedMember) {
      setAllocation(null)
      return
    }

    try {
      // Fetch member savings data to get accurate pokok balance
      const memberResponse = await api.get(`/members/${selectedMember.NBA}`)
      const memberWithSavings = memberResponse.data.data

      const response = await api.get('/settings/config')
      const data = response.data.data.data
      
      // Handle grouped response structure
      const getConfigValue = (key) => {
        let value = 0
        if (data.simpanan && data.simpanan[key]) {
          value = parseInt(data.simpanan[key].value || '0')
        }
        return value
      }

      // Get account codes - check all groups
      const getAccountCode = (key) => {
        let value = ''
        const groups = ['umum', 'akun', 'pendapatan', 'simpanan']
        for (const group of groups) {
          if (data[group] && data[group][key]) {
            value = data[group][key].value || ''
            break
          }
        }
        return value
      }

      const simpananWajib = getConfigValue('simpanan_wajib_default')
      const simpananPokokMax = getConfigValue('simpanan_pokok_default')
      const akunKas = getAccountCode('kode_akun_kas')
      const akunBank = getAccountCode('kode_akun_bank')
      const akunSimpananWajib = getAccountCode('kode_akun_simpanan_wajib')
      const akunSimpananPokok = getAccountCode('kode_akun_simpanan_pokok')
      const akunSimpananSukarela = getAccountCode('kode_akun_simpanan_sukarela')

      // Get current Simpanan Pokok balance from Simpanan table
      let currentPokokSaldo = 0
      if (memberWithSavings.simpanan && memberWithSavings.simpanan.length > 0) {
        const pokokAccount = memberWithSavings.simpanan.find(s => s.KodeJenis === 'POKOK' && s.Status === 'Aktif')
        currentPokokSaldo = pokokAccount ? (pokokAccount.Saldo || 0) : 0
      }

      const setoran = parseInt(jumlahSetor)
      let sisa = setoran
      let alokasiWajib = 0
      let alokasiPokok = 0
      let alokasiSukarela = 0

      // Priority 1: Simpanan Wajib (skip if already done this month)
      if (!wajibDoneThisMonth) {
        if (sisa >= simpananWajib) {
          alokasiWajib = simpananWajib
          sisa -= simpananWajib
        } else {
          alokasiWajib = sisa
          sisa = 0
        }
      }

      // Priority 2: Simpanan Pokok (up to maximum, check current balance)
      if (sisa > 0) {
        const pokokNeeded = simpananPokokMax - currentPokokSaldo
        if (pokokNeeded > 0) {
          alokasiPokok = Math.min(sisa, pokokNeeded)
          sisa -= alokasiPokok
        }
      }

      // Priority 3: Simpanan Sukarela (if remaining after pokok)
      if (sisa > 0) {
        alokasiSukarela = sisa
        sisa = 0
      }

      setAllocation({
        simpananWajib,
        simpananPokokMax,
        currentPokokSaldo,
        alokasiWajib,
        alokasiPokok,
        alokasiSukarela,
        total: alokasiWajib + alokasiPokok + alokasiSukarela,
        akunKas,
        akunBank,
        akunSimpananWajib,
        akunSimpananPokok,
        akunSimpananSukarela
      })
    } catch (error) {
      console.error('Error calculating allocation:', error)
    }
  }

  const handleProcess = async () => {
    if (!selectedMember || !jumlahSetor) {
      alert('Pilih anggota dan masukkan jumlah setoran')
      return
    }

    if (!sumberDana) {
      alert('Pilih sumber dana')
      return
    }

    setProcessing(true)
    try {
      const response = await api.post(`/savings/reguler/${selectedMember.NBA}/setoran`, {
        jumlahSetor: parseInt(jumlahSetor),
        sumberDana: sumberDana
      })
      setTransactionData(response.data.data)
      setShowConfirmation(true)
      // Reset jumlah setoran and allocation to prevent accidental clicks
      setJumlahSetor('')
      setAllocation(null)
    } catch (error) {
      console.error('Error processing setoran reguler:', error)
      alert(error.response?.data?.message || 'Gagal memproses setoran reguler')
    } finally {
      setProcessing(false)
    }
  }

  const handlePrint = () => {
    if (!transactionData) return

    // Format for PLQ20/draft printer (80mm width)
    const receiptContent = `
================================
  BUKTI SETORAN REGULER
================================
No Bukti : ${transactionData.noBukti || '-'}
NBA      : ${transactionData.nba}
Nama     : ${transactionData.nama || selectedMember?.Nama || '-'}
Tgl      : ${new Date().toLocaleDateString('id-ID')}
${transactionData.namaKoperasi || 'Koperasi'}
--------------------------------
SETORAN
--------------------------------
Jml Setor: Rp ${parseInt(transactionData.jumlahSetor).toLocaleString('id-ID')}
--------------------------------
ALOKASI
--------------------------------
Simpanan Wajib   : Rp ${transactionData.alokasiWajib?.toLocaleString('id-ID') || '0'}
Simpanan Pokok   : Rp ${transactionData.alokasiPokok?.toLocaleString('id-ID') || '0'}
Simpanan Sukarela: Rp ${transactionData.alokasiSukarela?.toLocaleString('id-ID') || '0'}
================================
  Tanda Tangan

(${transactionData.namaKoperasi || 'Koperasi'})
================================
`

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
      <head>
        <title>Bukti Setoran Reguler</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            margin: 0;
            padding: 10px;
            width: 80mm;
          }
          pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
          }
        </style>
      </head>
      <body>
        <pre>${receiptContent}</pre>
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const handleReset = () => {
    setShowConfirmation(false)
    setTransactionData(null)
    setSelectedMember(null)
    setJumlahSetor('')
    setAllocation(null)
  }

  const formatCurrency = (value) => {
    if (!value) return 'Rp 0'
    return `Rp ${parseInt(value).toLocaleString('id-ID')}`
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white">
          <h5 className="m-0 font-weight-bold">Setoran Reguler</h5>
        </div>
        <div className="card-body">
          {showConfirmation && transactionData ? (
            <div className="text-center">
              <div className="alert alert-success">
                <h4>Setoran Reguler Berhasil!</h4>
                <p>No Bukti: {transactionData.noBukti}</p>
                <p>Total Setoran: Rp {transactionData.jumlahSetor.toLocaleString('id-ID')}</p>
              </div>
              <div className="btn-group">
                <button className="btn btn-primary" onClick={handlePrint}><i className="fas fa-print mr-2"></i>Cetak Bukti</button>
                <button className="btn btn-secondary" onClick={handleReset}><i className="fas fa-redo mr-2"></i>Transaksi Baru</button>
              </div>
            </div>
          ) : (
            <>
              {error && <div className="alert alert-danger">{error}</div>}
              
              <div className="row mb-4">
                <div className="col-md-12">
                  <label className="small font-weight-bold">Pilih Anggota</label>
                  <select
                    ref={memberSelectRef}
                    className="form-control select2"
                    style={{ width: '100%' }}
                    disabled={loading}
                  >
                    <option value="">-- Pilih Anggota --</option>
                    {members.map((member) => (
                      <option key={member.NBA} value={member.NBA}>
                        {member.NBA} - {member.Nama}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedMember && (
                <>
                  <div className="alert alert-info mb-3">
                    <i className="fas fa-info-circle mr-2"></i>
                    <strong>Info Simpanan Wajib:</strong> {lastTglBayarWajib ? `Terakhir bayar: ${new Date(lastTglBayarWajib).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}` : 'Belum pernah membayar'}
                  </div>
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="small font-weight-bold">Jumlah Setoran</label>
                      <input 
                      type="number" 
                      className="form-control" 
                      value={jumlahSetor}
                      onChange={(e) => setJumlahSetor(e.target.value)}
                      placeholder="Masukkan jumlah setoran"
                      min="1"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small font-weight-bold">Sumber Dana</label>
                    <select 
                      className="form-control" 
                      value={sumberDana}
                      onChange={(e) => setSumberDana(e.target.value)}
                    >
                      <option value="kas">Kas</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>
                </div>
                </>
              )}

              {allocation && (
                <div className="card card-outline card-info mt-3">
                  <div className="card-header">
                    <h3 className="card-title">Preview Alokasi Dana</h3>
                  </div>
                  <div className="card-body">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Komponen</th>
                          <th>Kode Akun</th>
                          <th className="text-right">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Simpanan Wajib</td>
                          <td>{allocation.akunSimpananWajib || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.alokasiWajib)}</td>
                        </tr>
                        <tr>
                          <td>Simpanan Pokok</td>
                          <td>{allocation.akunSimpananPokok || '-'}</td>
                          <td className="text-right">{formatCurrency(allocation.alokasiPokok)}</td>
                        </tr>
                        <tr className="table-light">
                          <td colSpan="3" className="small">
                            Saldo Pokok Saat Ini: {formatCurrency(allocation.currentPokokSaldo)} / Maksimal: {formatCurrency(allocation.simpananPokokMax)}
                          </td>
                        </tr>
                        {allocation.alokasiSukarela > 0 && (
                          <tr className="table-success">
                            <td><strong>Simpanan Sukarela (kelebihan)</strong></td>
                            <td>{allocation.akunSimpananSukarela || '-'}</td>
                            <td className="text-right"><strong>{formatCurrency(allocation.alokasiSukarela)}</strong></td>
                          </tr>
                        )}
                        <tr className="table-info">
                          <td><strong>Sumber Dana ({sumberDana === 'kas' ? 'Kas' : 'Bank'})</strong></td>
                          <td>{sumberDana === 'kas' ? allocation.akunKas : allocation.akunBank || '-'}</td>
                          <td className="text-right"><strong>{formatCurrency(parseInt(jumlahSetor))}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedMember && jumlahSetor && (
                <div className="mt-3">
                  <button 
                    className="btn btn-success btn-lg btn-block" 
                    onClick={handleProcess}
                    disabled={processing || !allocation}
                  >
                    {processing ? 'Memproses...' : 'Proses Setoran'}
                  </button>
                </div>
              )}

              <div className="alert alert-warning mt-3">
                <strong>Aturan Transaksi:</strong><br/>
                - Jumlah setoran akan dialokasikan ke: Simpanan Wajib, Simpanan Pokok, dan Simpanan Sukarela<br/>
                - Prioritas alokasi: Simpanan Wajib &gt; Simpanan Pokok &gt; Simpanan Sukarela<br/>
                - Kelebihan setoran akan masuk ke Simpanan Sukarela
              </div>
            </>
          )}
        </div>
      </div>

      {showConfirmation && transactionData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bukti Setoran Reguler</h5>
                <button type="button" className="close" onClick={() => {
                  setShowConfirmation(false)
                  setTransactionData(null)
                }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle mr-2"></i>
                  Setoran reguler berhasil diproses!
                </div>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {transactionData.nba}</td>
                    </tr>
                    <tr>
                      <td><strong>Nama</strong></td>
                      <td>: {transactionData.nama}</td>
                    </tr>
                    <tr>
                      <td><strong>Jumlah Setor</strong></td>
                      <td>: {formatCurrency(transactionData.jumlahSetor)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Wajib</strong></td>
                      <td>: {formatCurrency(transactionData.alokasiWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Pokok</strong></td>
                      <td>: {formatCurrency(transactionData.alokasiPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Sukarela</strong></td>
                      <td>: {formatCurrency(transactionData.alokasiSukarela)}</td>
                    </tr>
                    <tr>
                      <td><strong>No Bukti</strong></td>
                      <td>: {transactionData.noBukti}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowConfirmation(false)
                  setTransactionData(null)
                }}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePrint}
                >
                  <i className="fas fa-print mr-2"></i>
                  Cetak Bukti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SetoranReguler
